import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import WeekPicker, { Week } from "../../components/week-picker";
import { dateFormatter, formatDuration } from "../../core/utils";
import ToastComponent from "../../components/toast";
import PermissionGate from "../../core/layouts/permission-gate";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../redux/store";
import {
  getErrorState,
  setBreadcrumbState,
} from "../../redux/features/common-slice";
import { getUserState } from "../../redux/features/auth-slice";
import {
  clearDeleteReportState,
  clearUpdateReportState,
  getDeleteReportState,
  getUpdateReportState,
  getUserWeeklyReportsState,
  getUserWeeklyReportsStateAction,
} from "../../redux/features/report-slice";
import paths from "../../navigation/paths";
import { UserRole } from "../../redux/features/user-slice";
import SwitchTab from "../../components/switch-tab";
import ReportsTab from "./components/reports-tab";
import SummaryTab from "./components/summary-tab";
import {
  clearNewWeeklySummaryState,
  clearUpdateWeeklySummaryState,
  getNewWeeklySummaryState,
  getUpdateWeeklySummaryState,
} from "../../redux/features/weekly-summary-slice";

const tabs = ["Reports", "1:1 Weekly Reports"];

export default function WeeklyReportsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get("tab") ?? tabs[1];

  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector(getUserState).data;
  const userWeeklyReports = useSelector(getUserWeeklyReportsState).data;
  const updateReport = useSelector(getUpdateReportState).data;
  const deleteReport = useSelector(getDeleteReportState).data;
  const newWeeklySummaryState = useSelector(getNewWeeklySummaryState);
  const updateWeeklySummaryState = useSelector(getUpdateWeeklySummaryState);
  const errorState = useSelector(getErrorState);

  const [weekRange, setWeekRange] = useState<Week>();
  const [tab, setTab] = useState<string>(tabParam);
  const [totalDuration, setTotalDuration] = useState<number>(0);

  const fetchUserWeeklyReports = async () => {
    if (weekRange) {
      await dispatch(
        getUserWeeklyReportsStateAction({
          start: dateFormatter(weekRange.firstDay, "yyyy-MM-dd"),
          end: dateFormatter(weekRange.lastDay, "yyyy-MM-dd"),
        })
      );
    }
  };

  const handleSuccessToast = useCallback(
    (message: string, clearAction: any) => {
      toast(message, { type: "success" });
      setTimeout(() => {
        dispatch(clearAction());
      }, 2000);
    },
    [dispatch]
  );

  useEffect(() => {
    dispatch(
      setBreadcrumbState([
        { id: "reports", path: null, title: "Reports" },
        { id: paths.WEEKLY_REPORTS, path: null, title: "Weekly Reports" },
      ])
    );
  }, []);

  useEffect(() => {
    const processedTags = new Set();
    const dur = userWeeklyReports.reduce((sum, item) => {
      const progressSum = Object.values(item.progress).reduce(
        (subSum, subItem) => {
          const durationForProgress = subItem.reports.reduce(
            (reportSum, report) => {
              if (!report.tag || !processedTags.has(report.tag)) {
                if (report.tag) {
                  processedTags.add(report.tag);
                }
                return reportSum + report.duration;
              }
              return reportSum;
            },
            0
          );
          return subSum + durationForProgress;
        },
        0
      );
      return sum + progressSum;
    }, 0);
    setTotalDuration(dur);
  }, [userWeeklyReports]);

  useEffect(() => {
    if (weekRange) {
      fetchUserWeeklyReports();
    }
  }, [weekRange]);

  useEffect(() => {
    if (updateReport && updateReport.success) {
      handleSuccessToast(updateReport.message, clearUpdateReportState);
    }
  }, [updateReport, handleSuccessToast]);

  useEffect(() => {
    if (deleteReport && deleteReport.success) {
      handleSuccessToast(deleteReport.message, clearDeleteReportState);
    }
  }, [deleteReport, handleSuccessToast]);

  useEffect(() => {
    if (newWeeklySummaryState.data && newWeeklySummaryState.data.success) {
      handleSuccessToast(
        newWeeklySummaryState.data.message,
        clearNewWeeklySummaryState
      );
    }
  }, [newWeeklySummaryState, handleSuccessToast]);

  useEffect(() => {
    if (
      updateWeeklySummaryState.data &&
      updateWeeklySummaryState.data.success
    ) {
      handleSuccessToast(
        updateWeeklySummaryState.data.message,
        clearUpdateWeeklySummaryState
      );
    }
  }, [updateWeeklySummaryState, handleSuccessToast]);

  useEffect(() => {
    if (errorState) {
      if (errorState.redirect) {
        navigate(errorState.redirect.destination, {
          replace: errorState.redirect.permanent,
        });
        return;
      }
      toast(errorState.message, { type: "error" });
    }
  }, [errorState]);

  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("tab", tab);
    setSearchParams(newParams, { replace: true });
  }, [tab, searchParams, setSearchParams]);

  return (
    <PermissionGate
      categories={[]}
      roles={[
        UserRole.Manager,
        UserRole.Leader,
        UserRole.Contributor,
        UserRole.Member,
      ]}
    >
      <Helmet title="Weekly Reports" />
      <div className="flex flex-col h-full">
        <div className="flex justify-between items-center pb-4">
          <div className="flex flex-col">
            <label className="text-text-title">Total duration</label>
            <div className="space-x-2 flex items-center">
              <label className="text-text-primary text-xl">
                {formatDuration(totalDuration)}
              </label>
              <label>•</label>
              <label className="text-text-primary text-sm">{`${(
                (totalDuration / 40) *
                100
              ).toFixed(1)}%`}</label>
            </div>
          </div>
          <div className="flex space-x-4">
            <SwitchTab options={tabs} tab={tab} setTab={setTab} />
            <WeekPicker onChange={(week) => setWeekRange(week)} />
          </div>
        </div>
        {tab === tabs[0] ? (
          <ReportsTab
            reports={userWeeklyReports}
            onRefresh={fetchUserWeeklyReports}
          />
        ) : weekRange && user ? (
          <SummaryTab user={user} weekRange={weekRange} />
        ) : (
          <div />
        )}
        <ToastComponent id="weeklyReports" />
      </div>
    </PermissionGate>
  );
}
